/**
 * Карточка допработы/учёбки на доске ОО (R8.7). 13.10: вынесена из OoAssignScreen.tsx
 * без изменений поведения — экран-гигант не может расти, а карточке нужна правка учёбки.
 */
import { useEffect, useRef, useState } from "react";
import type { BoardOperator } from "../../hooks/useOoDay";
import type { ExtraWorkGroup } from "../../hooks/useExtraWork";
import { todayBusiness, nowUtc5 } from "../../utils/date";
import { NumPadPop, TextPad, TimePicker } from "../../components/shared";
import { useChipDrag } from "./useChipDrag";
import { useClampPop } from "./useClampPop";
import { canWholeWork } from "./canWhole";
import { Plus, Xmark, ActSend, ActTrash, ActStudy } from "./v8icons";
import { StudyEditSheet, type StudyEditCtx } from "./StudyEditSheet";
import css from "./board.module.css";
import sx from "./studyEdit.module.css";

/** Допработа уже прошла по времени (UTC+5) — урна с усиленным подтверждением. */
export function ewDone(g: ExtraWorkGroup, date: Date): boolean {
  const today = todayBusiness();
  if (date.getTime() < today.getTime()) return true;
  if (date.getTime() > today.getTime()) return false;
  const [h, m] = g.timeTo.split(":").map(Number); const now = nowUtc5();
  return (h ?? 0) * 60 + (m ?? 0) <= now.getUTCHours() * 60 + now.getUTCMinutes();
}

/* ── R8.7 доп.работа — ОДНА строка (26.08, владелец): жесты как у игровых чипов
   (long-press → меню, смахнуть → убрать, лассо → выделить/копировать), время и
   филиал белым, без иконки-ключа. Урна двухшаговая; «сделанную» просто так не
   удалить. Принятые оператором чипы — белая рамка. */
export function ExtraWorkCard({ group, operators, taken, done, lassoSel, lassoDown, onRemoveOp, onRemoveAll, onAddOp, onPatch, onFactOk, onSend, onRate, study }: {
  group: ExtraWorkGroup; operators: BoardOperator[];
  /** Все, кто уже в этом слоте — и в сохранённой, и в отправленной карточке (26.09). */
  taken: Set<string>;
  /** работа уже прошла по времени — удаление с усиленным подтверждением */
  done: boolean;
  lassoSel: Set<string>; lassoDown: (e: React.PointerEvent) => void;
  onRemoveOp: (rowId: number) => void;
  onRemoveAll: (g: ExtraWorkGroup) => void;
  onAddOp: (g: ExtraWorkGroup, opId: string) => void;
  onPatch: (g: ExtraWorkGroup, patch: { time_from?: string; time_to?: string; description?: string }) => void;
  /** Зачесть фактическое время из отчёта исполнителя (11.09) — до этого платим по плану. */
  onFactOk: (rowId: number) => void;
  /** 26.09: отправить сохранённую карточку (снять черновик, написать каждому). */
  onSend: (g: ExtraWorkGroup) => Promise<boolean>;
  /** 27.09 (владелец): ставка куратора ₽/ч — удержанием его чипа (итог пересчитается по часам). */
  onRate: (rowId: number, rate: number) => void;
  /** 13.10: окно правки учёбки (✎) — день, филиалы, карточки дня, итог. */
  study: StudyEditCtx;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [ratePad, setRatePad] = useState<{ rowId: number; name: string; value: number } | null>(null);
  const [cancelArm, setCancelArm] = useState(false); const cancelT = useRef(0);
  useEffect(() => () => window.clearTimeout(cancelT.current), []);
  const chipEls = useRef(new Map<number, HTMLElement>());
  const [sending, setSending] = useState(false);
  const [pickTime, setPickTime] = useState(false);
  const [editDesc, setEditDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(group.description);
  const [opPick, setOpPick] = useState(false);
  const [chipMenu, setChipMenu] = useState<number | null>(null);
  const [delArm, setDelArm] = useState(false);
  const delT = useRef(0);
  const [btnDel, setBtnDel] = useState(false); const btnLp = useRef(0); const btnLpStart = useRef<{ x: number; y: number } | null>(null); const btnLpFired = useRef(false);
  const gid = `ew:${group.key}`;
  const { chipDown, chipMove, chipRelease } = useChipDrag({
    /* 27.09 (владелец: «установить зарплату куратору лонгпрессом по чипу куратора — рубли в час»):
       удержание чипа куратора — сразу ставка; убрать его из учёбки — смахнуть, как любой чип. */
    onMenu: (id) => {
      const cur = group.ops.find((x) => x.rowId === Number(id) && x.kind === "training_curator");
      if (cur) setRatePad({ rowId: cur.rowId, name: cur.operatorName, value: cur.rate });
      else setChipMenu(Number(id));
    },
    onSwipeAway: (id) => onRemoveOp(Number(id)),
  });
  useEffect(() => { if (chipMenu == null) return; // клик мимо — закрыть меню (канон chipMenu)
    const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.chipMenu}`)) setChipMenu(null); };
    document.addEventListener("pointerdown", onDown, true); return () => document.removeEventListener("pointerdown", onDown, true);
  }, [chipMenu]);
  const free = operators.filter((o) => !taken.has(o.id));
  const chipMenuRef = useClampPop<HTMLDivElement>(chipMenu); const opPickRef = useClampPop<HTMLDivElement>(opPick || null);
  const armDelete = () => {
    if (delArm) { window.clearTimeout(delT.current); setDelArm(false); onRemoveAll(group); return; }
    setDelArm(true); delT.current = window.setTimeout(() => setDelArm(false), 3000);
  };
  return (
    <article className={`${css.ewCard} ${group.training ? css.ewStudy : ""} ${group.draft ? css.ewDraft : ""}`} data-gid={gid}>
      <div className={css.ewRow}>
        <button type="button" className={css.ewTime} title="Тап — изменить время" onClick={() => setPickTime(true)}>{group.timeFrom}–{group.timeTo}</button>
        {group.branchId ? <span className={css.ewBranch}>{group.branchId}</span> : null}
        {/* 27.09 (владелец): вместо чипа «учёбка» — значок с академической шапочкой */}
        {group.training ? <span className={css.ewStudyIco} role="img" aria-label="Учёбка" title="Учёбка: ученикам не оплачивается, куратору — как допработа"><ActStudy /></span> : null}
        <button type="button" className={`${css.ewDescBtn} ${group.training ? sx.descTight : ""}`} title="Тап — изменить описание" onClick={() => { setDescDraft(group.description); setEditDesc(true); }}>{group.description || "—"}</button>
        {/* 11.09: такси из отчётов исполнителей — ОО видит, за что вернутся деньги. */}
        {group.taxi > 0 ? <span className={css.ewTaxi} title={`Такси заявил: ${group.taxiWho.join(", ")}`}>🚕 ₽{group.taxi}</span> : null}
        {/* Фактическое время разошлось с планом: до подтверждения платим по плану. */}
        {group.factPending.map((f) => (
          <button key={f.rowId} type="button" className={css.ewFact}
            title={`${f.who}: работал ${f.from}–${f.to}${f.text ? ` — ${f.text}` : ""}. Тап — зачесть по факту`}
            onClick={() => onFactOk(f.rowId)}>{f.from}–{f.to}?</button>
        ))}
        {/* 27.09 (владелец: «плюсик в обучении — точно так же, как при расстановке: всегда сверху справа») */}
        <span className={css.ewActs}>
          {group.draft ? (
            <button type="button" className={css.ewSend} disabled={sending} aria-label="Отправить операторам"
              title="Отправить: операторы увидят работу и получат сообщение в Telegram"
              onClick={() => { setSending(true); void onSend(group).then((ok) => { if (!ok) setSending(false); }); }}><ActSend /></button>
          ) : null}
          {/* 13.10 (владелец: «учёбкам в расстановке у ОО нужно иметь возможность отредактировать»): ✎ — окно
              правки времени, темы, филиала, куратора и ставки. Прежние жесты карточки — как были. */}
          {group.training ? <button type="button" className={sx.ewEdit} aria-label="Изменить учёбку"
            title="Изменить учёбку: время, тема, филиал, куратор и его ставка" onClick={() => setEditOpen(true)}>✎</button> : null}
          {/* 27.09 (владелец): отмена — красный квадратик с крестиком рядом с «+». Два тапа: первый
              заливает красным («точно?»), второй отменяет; отправленной — участникам «Учёбка отменена». */}
          {group.training ? (
            <button type="button" className={`${css.ewCancel} ${cancelArm ? css.ewCancelArm : ""}`}
              aria-label={cancelArm ? "Подтвердить отмену учёбки" : "Отменить учёбку"}
              title={cancelArm ? "Ещё раз — отменить учёбку" : group.draft ? "Отменить учёбку (не отправлена — без сообщений)" : "Отменить учёбку — участникам придёт сообщение"}
              onClick={() => {
                if (cancelArm) { window.clearTimeout(cancelT.current); setCancelArm(false); onRemoveAll(group); return; }
                setCancelArm(true); cancelT.current = window.setTimeout(() => setCancelArm(false), 3000);
              }}><Xmark /></button>
          ) : null}
          {/* одна кнопка «+»/урна (26.08, владелец): long-press переключает режим */}
          <span className={css.addOpWrap}>
            <button type="button" className={btnDel ? `${css.ewDel} ${delArm ? css.ewDelArm : ""}` : css.ewAdd}
              aria-label={btnDel ? "Удалить доп.работу (long-press — вернуть «+»)" : "Добавить участника (long-press — урна)"}
              title={btnDel ? (done ? "Работа уже сделана — удаление в два тапа; long-press — вернуть «+»" : "Удалить целиком (два тапа); long-press — вернуть «+»") : "Добавить участника; long-press — урна"}
              onPointerDown={(e) => { btnLpFired.current = false; btnLpStart.current = { x: e.clientX, y: e.clientY }; btnLp.current = window.setTimeout(() => { btnLpFired.current = true; setBtnDel((v) => !v); setDelArm(false); setOpPick(false); }, 450); }}
              onPointerMove={(e) => { const s = btnLpStart.current; if (s && (Math.abs(e.clientX - s.x) > 8 || Math.abs(e.clientY - s.y) > 8)) window.clearTimeout(btnLp.current); }}
              onPointerUp={() => window.clearTimeout(btnLp.current)} onPointerLeave={() => window.clearTimeout(btnLp.current)} onContextMenu={(e) => e.preventDefault()}
              onClick={() => { if (btnLpFired.current) { btnLpFired.current = false; return; } if (btnDel) armDelete(); else setOpPick((v) => !v); }}
            >{btnDel ? (delArm ? (done ? "Сделана! Точно?" : "Точно?") : <ActTrash />) : <Plus />}</button>
            {opPick ? (
              <div className={css.mkOplist} ref={opPickRef}>
                {free.length === 0 ? <div className={css.inlineEmpty}>Все уже в работе</div>
                  : free.map((o) => {
                    /* 27.09 (владелец): как у «+» игры — зелёный/красный квадратик «свободен ли всё время работы по графику» */
                    const can = canWholeWork(o, group.timeFrom, group.timeTo);
                    const tip = can ? "по графику свободен всё время" : "по графику НЕ может всё время";
                    return <button key={o.id} type="button" className={css.opItem} title={tip} onClick={() => { onAddOp(group, o.id); setOpPick(false); }}><span className={css.opName}>{o.name}</span><span className={`${css.opSq} ${can ? css.opSqOk : css.opSqNo}`} aria-label={tip} /></button>;
                  })}
              </div>
            ) : null}
          </span>
        </span>
      </div>
      <div className={css.ewOps}>
        {/* 26.09 (владелец): сохранено — операторы не видят и сообщений не получали, пока не отправишь.
            27.09: во второй строке — в шапке на телефоне не помещалось вместе с «+», ✕ и отправкой. */}
        {group.draft ? <span className={css.ewDraftTag} title="Сохранено: операторы не видят, пока не отправишь">не отправлено</span> : null}
        {group.ops.map((o) => (
          <span key={o.rowId} className={css.chipWrap}>
            <span
              data-op={o.operatorId}
              className={`${css.ewChip} ${o.accepted ? css.ewAcc : ""} ${o.kind === "training_curator" ? css.ewCurator : ""} ${lassoSel.has(`${gid}:${o.operatorId}`) ? css.sel : ""}`}
              ref={(el) => { if (el) chipEls.current.set(o.rowId, el); else chipEls.current.delete(o.rowId); }}
              title={o.kind === "training_curator" ? `Куратор — оплачивается как допработа, ${o.rate} ₽/ч · удержание — изменить ставку` : o.kind === "training" ? `Ученик — без оплаты${o.accepted ? " · видел ✓" : ""}` : o.accepted ? "Оператор видел допработу ✓" : undefined}
              onPointerDown={(e) => (lassoSel.has(`${gid}:${o.operatorId}`) ? lassoDown(e) : chipDown(String(o.rowId), e))}
              onPointerMove={(e) => chipMove(String(o.rowId), e)}
              onPointerUp={chipRelease} onPointerCancel={chipRelease} onContextMenu={(e) => e.preventDefault()}
            >{o.operatorName}{o.kind === "training_curator" ? <span className={css.ewRate}> · {o.rate}₽/ч</span> : null}</span>
            {chipMenu === o.rowId ? (
              <div className={css.chipMenu} ref={chipMenuRef}>
                <button type="button" className={css.chipMenuItem} onClick={() => { setChipMenu(null); onRemoveOp(o.rowId); }}>{group.training ? "Убрать из учёбки" : "Убрать из допработы"}</button>
                <button type="button" className={`${css.chipMenuItem} ${css.chipMenuClose}`} onClick={() => setChipMenu(null)}>Закрыть</button>
              </div>
            ) : null}
          </span>
        ))}
      </div>
      <NumPadPop
        open={!!ratePad} anchor={ratePad ? chipEls.current.get(ratePad.rowId) ?? null : null}
        label={ratePad ? `Ставка куратора ₽/ч · ${ratePad.name}` : ""} mode="money" value={ratePad?.value ?? 0} max={99999}
        onCommit={(v) => { if (ratePad && v !== ratePad.value) onRate(ratePad.rowId, v); }} onClose={() => setRatePad(null)}
        extraKeys={[{ label: "Записать", onPress: (v) => { if (ratePad) onRate(ratePad.rowId, v); setRatePad(null); } }]}
      />
      {/* 09.09 (владелец): системная клавиатура Андроида заменена на нашу — как в комментариях */}
      <TextPad
        open={editDesc}
        label="Что за работа"
        multiline
        maxLength={200}
        placeholder="Напр. звук на ТАРО + сбор реквизита"
        value={descDraft}
        onCommit={(v) => { setDescDraft(v); const s = v.trim(); if (s && s !== group.description) onPatch(group, { description: s }); }}
        onClose={() => setEditDesc(false)}
      />
      {pickTime ? (
        <TimePicker
          open mode="range" step={5} label="Доп — время от и до"
          value={`${group.timeFrom}–${group.timeTo}`}
          onCommit={(v) => { const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/); if (m) onPatch(group, { time_from: m[1], time_to: m[2] }); setPickTime(false); }}
          onCancel={() => setPickTime(false)}
        />
      ) : null}
      {editOpen ? <StudyEditSheet group={group} operators={operators} {...study} onClose={() => setEditOpen(false)} /> : null}
    </article>
  );
}
